import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatWhen, lifecycleLabel } from "@/lib/format";
import { newIdempotencyKey } from "@/lib/idempotency";
import { ApiClientError, type Lease } from "@/lib/types";
import { AuthStatePanels } from "@/components/AuthState";
import { Button } from "@/components/Button";
import { ConfirmSheet } from "@/components/ConfirmSheet";
import { DemoChip } from "@/components/DemoChip";
import { StatePanel } from "@/components/StatePanel";
import { StatusRail } from "@/components/StatusRail";
import { useLocale } from "@/locale/LocaleContext";
import shell from "@/components/shell.module.css";
import ui from "@/components/ui.module.css";

export function LeaseDetailPage() {
  const { leaseId = "" } = useParams();
  const { m, locale } = useLocale();
  const navigate = useNavigate();
  const [lease, setLease] = useState<Lease | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiClientError | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.leases();
        if (!alive) return;
        const found = res.items.find((item) => item.id === leaseId) ?? null;
        setLease(found);
        setError(
          found ? null : new ApiClientError(404, "not_found", m.leases.notFound),
        );
      } catch (e) {
        if (!alive) return;
        setError(e instanceof ApiClientError ? e : new ApiClientError(0, "error", m.states.error));
      } finally {
        if (alive) setLoading(false);
      }
    };
    void load();
    const timer = window.setInterval(() => void load(), 4000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [leaseId, m.leases.notFound, m.states.error]);

  async function onEnd() {
    setBusy(true);
    setActionError(null);
    try {
      const op = await api.endLease(leaseId, newIdempotencyKey());
      setConfirm(false);
      navigate(`/app/operations/${op.id}`);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : m.states.error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthStatePanels
      loading={loading}
      error={error && !lease ? error : null}
      empty={!lease ? <StatePanel kind="empty" title={m.leases.notFound} /> : null}
    >
      {lease ? (
        <div>
          <p className={ui.meta}>
            <Link to="/app/machines">← {m.leases.back}</Link>
          </p>
          <h1 className={shell.pageTitle}>{m.leases.title}</h1>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <DemoChip />
            <span className={ui.meta}>{m.leases.noSsh}</span>
          </div>

          <div className={ui.row} style={{ cursor: "default", marginTop: "1rem" }}>
            <StatusRail
              state={lease.state}
              pulse={lease.state === "ending" || lease.state === "provisioning"}
            />
            <div>
              <strong>{lifecycleLabel(m, lease.state)}</strong>
              <div className={`mono ${ui.meta}`}>lease {lease.id}</div>
              <div className={`mono ${ui.meta}`}>machine {lease.machine_id}</div>
              <div className={ui.meta}>
                {formatWhen(lease.created_at, locale)} → {formatWhen(lease.updated_at, locale)}
              </div>
            </div>
          </div>

          {lease.state === "ending" ? (
            <StatePanel kind="stale" title={m.leases.endingTitle} message={m.leases.endingBody} />
          ) : null}

          {lease.state !== "ended" && lease.state !== "canceled" && lease.state !== "failed" ? (
            <div className={ui.actions} style={{ justifyContent: "flex-start", marginTop: "1rem" }}>
              <Button
                variant="danger"
                onClick={() => {
                  setAck(false);
                  setConfirm(true);
                }}
              >
                {m.leases.endAction}
              </Button>
            </div>
          ) : null}

          <ConfirmSheet
            open={confirm}
            title={m.leases.endTitle}
            confirmLabel={m.leases.endAction}
            danger
            confirmDisabled={!ack}
            busy={busy}
            onClose={() => setConfirm(false)}
            onConfirm={() => void onEnd()}
          >
            <p>{m.leases.endBody}</p>
            <label style={{ display: "flex", gap: "0.55rem" }}>
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              <span>{m.leases.endAck}</span>
            </label>
            {actionError ? <p className={ui.fieldError}>{actionError}</p> : null}
          </ConfirmSheet>
        </div>
      ) : null}
    </AuthStatePanels>
  );
}
